import { Dropdown } from "@circle-react-uikit/Dropdown";
import { IconButton } from "@circle-react-uikit/HeaderV3/IconButton";
import type { Notification } from "@circle-react/types/Notification";

import { t } from "@/i18n-js/instance";

import { useNotificationsActions } from "../../../useHeaderNotificationsApi";
import {
  FOLLOWABLE_NOTIFICATION_TYPES,
  NOTIFICATION_ACTION_MAPPER,
  NOTIFICATION_ARCHIVED_STATUS,
} from "../constants";

export interface ActionsUIKitComponentProps {
  notification: Notification;
}

export const ActionsUIKitComponent = ({
  notification,
}: ActionsUIKitComponentProps) => {
  const canUnfollowPost = Boolean(notification.post_follower_id);
  const canFollowPost =
    FOLLOWABLE_NOTIFICATION_TYPES.includes(notification.notifiable_type) &&
    !notification.post_follower_id;
  const canArchiveNotification =
    notification.notification_group !== NOTIFICATION_ARCHIVED_STATUS;
  const hasNotificationBeenRead = notification.read_at;

  const {
    mutate: performNotificationAction,
    isLoading: isNotificationActionBeingPerformed,
  } = useNotificationsActions();

  const isLiveAction = notification.action === "live_stream_went_live";
  const shouldSkipNotificationDropdown =
    !canUnfollowPost &&
    !canArchiveNotification &&
    hasNotificationBeenRead !== null &&
    isLiveAction;

  if (shouldSkipNotificationDropdown) {
    return null;
  }

  return (
    <Dropdown
      size="medium"
      direction="bottom-end"
      className="hidden size-6 group-hover:inline-block"
      button={
        <IconButton name="16-menu-dots-horizontal" ariaLabel={t("actions")} />
      }
      transitionWrapperClassName="z-30"
    >
      {!hasNotificationBeenRead && (
        <Dropdown.ItemLink
          onClick={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
            performNotificationAction({
              id: notification.id,
              action: NOTIFICATION_ACTION_MAPPER.markAsRead,
            });
          }}
          disabled={isNotificationActionBeingPerformed}
        >
          {t("notifications.actions.mark_as_read")}
        </Dropdown.ItemLink>
      )}
      {canUnfollowPost && (
        <Dropdown.ItemLink
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            performNotificationAction({
              postId: notification.post_id,
              followerId: notification.post_follower_id,
              action: NOTIFICATION_ACTION_MAPPER.unfollow,
            });
          }}
          disabled={isNotificationActionBeingPerformed}
        >
          {t("notifications.actions.unfollow_post")}
        </Dropdown.ItemLink>
      )}
      {canFollowPost && (
        <Dropdown.ItemLink
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            performNotificationAction({
              postId: notification.post_id,
              action: NOTIFICATION_ACTION_MAPPER.follow,
            });
          }}
          disabled={isNotificationActionBeingPerformed}
        >
          {t("notifications.actions.follow_post")}
        </Dropdown.ItemLink>
      )}
      {canArchiveNotification && (
        <Dropdown.ItemLink
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            performNotificationAction({
              id: notification.id,
              action: NOTIFICATION_ACTION_MAPPER.archive,
            });
          }}
          disabled={isNotificationActionBeingPerformed}
        >
          {t("notifications.actions.archive")}
        </Dropdown.ItemLink>
      )}
    </Dropdown>
  );
};
